/** 文字種変換や書式化で使う共通文字列ユーティリティ群。 */
/**
 * 全角数字を半角数字に変換する
 * フォーム入力で全角数字が入力された場合に半角に正規化するために使用する。
 *
 * @param str 変換対象の文字列
 * @returns 全角数字が半角に変換された文字列
 */
const toHalfNumber = (str: string) => {
  return str.replace(/[０-９]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
  });
};

/**
 * 半角数字を全角数字に変換する
 * 表示用に全角文字へ統一する場面で使用する。
 *
 * @param str 変換対象の文字列
 * @returns 半角数字が全角に変換された文字列
 */
const toZenkakuNumber = (str: string) => {
  return str.replace(/[0-9]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
  });
};

/**
 * 指定された年月が現在月より過去かどうかを判定する
 * 請求明細の表示制御などで、過去月のデータかどうかを判断する際に使用する。
 *
 * @param year 年（4桁の文字列）
 * @param month 月（2桁の文字列、ゼロ埋め）
 * @returns 過去月の場合true
 */
const isPastMonth = (year: string, month: string) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
  return `${year}${month}` < `${currentYear}${currentMonth}`;
};

/**
 * 英字を全角文字列に変換する。
 * @param str  対象文字列
 * @returns 変換結果
 */
const toZenkakuAlpha = (str: string) => {
  if (!str) return '';
  return str.replace(/[A-Za-z]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
  });
};

/**
 * 英数字を全角文字列に変換する。
 * @param str  対象文字列
 * @returns 変換結果
 */
const toZenkakuAlphaNumber = (str: string) => {
  if (!str) return '';
  let v = str.replace(/[A-Za-z]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
  });
  v = toZenkakuNumber(v);
  return v;
};

/**
 * 指定文字列をスネークケースに変換する。
 * @param str 対象文字列
 * @returns 変換結果
 */
const toSnakeCase = (str: string): string => {
  if (!str) return '';
  const match = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g);
  if (!match) return '';
  return match.map((x) => x.toLowerCase()).join('_');
};

/**
 * カンマを除去する
 * 数値文字列のフォーマット変換（表示用→計算用）に使用する。
 *
 * @param str カンマを含む文字列
 * @returns カンマが除去された文字列
 */
const removeCommas = (str: string): string => str.replace(/,/g, '');

/**
 * 数値に3桁区切りのカンマを付与する
 * 金額表示などで使用する。
 *
 * @param num カンマを付与する数値
 * @returns 3桁区切りカンマ付きの文字列
 */
const addCommas = (num: number): string => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * 半角英数字・記号を全角に変換する
 * セコム向けなど、全角統一が必要な外部連携時に使用する。
 *
 * @param str 変換対象の文字列
 * @returns 全角に変換された文字列
 */
const toZenkakuAlphaNumMark = (str: string) => {
  return str
    .replace(/ /g, '　')
    .replace(/"/g, '”')
    .replace(/'/g, '’')
    .replace(/`/g, '‘')
    .replace(/\\/g, '￥')
    .replace(/~/g, '～')
    .replace(/[!-~]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
    });
};

/**
 * 文字列を全角に変換する
 */
const toFullWidth = (str: string) => {
  // 半角カナ・ASCII・空白を一括で全角へ寄せ、表示上の文字幅を揃える。
  const kanaMap: { [key: string]: string } = {
    ｶﾞ: 'ガ',
    ｷﾞ: 'ギ',
    ｸﾞ: 'グ',
    ｹﾞ: 'ゲ',
    ｺﾞ: 'ゴ',
    ｻﾞ: 'ザ',
    ｼﾞ: 'ジ',
    ｽﾞ: 'ズ',
    ｾﾞ: 'ゼ',
    ｿﾞ: 'ゾ',
    ﾀﾞ: 'ダ',
    ﾁﾞ: 'ヂ',
    ﾂﾞ: 'ヅ',
    ﾃﾞ: 'デ',
    ﾄﾞ: 'ド',
    ﾊﾞ: 'バ',
    ﾋﾞ: 'ビ',
    ﾌﾞ: 'ブ',
    ﾍﾞ: 'ベ',
    ﾎﾞ: 'ボ',
    ﾊﾟ: 'パ',
    ﾋﾟ: 'ピ',
    ﾌﾟ: 'プ',
    ﾍﾟ: 'ペ',
    ﾎﾟ: 'ポ',
    ｳﾞ: 'ヴ',
    ﾜﾞ: 'ヷ',
    ｦﾞ: 'ヺ',
    ｱ: 'ア',
    ｲ: 'イ',
    ｳ: 'ウ',
    ｴ: 'エ',
    ｵ: 'オ',
    ｶ: 'カ',
    ｷ: 'キ',
    ｸ: 'ク',
    ｹ: 'ケ',
    ｺ: 'コ',
    ｻ: 'サ',
    ｼ: 'シ',
    ｽ: 'ス',
    ｾ: 'セ',
    ｿ: 'ソ',
    ﾀ: 'タ',
    ﾁ: 'チ',
    ﾂ: 'ツ',
    ﾃ: 'テ',
    ﾄ: 'ト',
    ﾅ: 'ナ',
    ﾆ: 'ニ',
    ﾇ: 'ヌ',
    ﾈ: 'ネ',
    ﾉ: 'ノ',
    ﾊ: 'ハ',
    ﾋ: 'ヒ',
    ﾌ: 'フ',
    ﾍ: 'ヘ',
    ﾎ: 'ホ',
    ﾏ: 'マ',
    ﾐ: 'ミ',
    ﾑ: 'ム',
    ﾒ: 'メ',
    ﾓ: 'モ',
    ﾔ: 'ヤ',
    ﾕ: 'ユ',
    ﾖ: 'ヨ',
    ﾗ: 'ラ',
    ﾘ: 'リ',
    ﾙ: 'ル',
    ﾚ: 'レ',
    ﾛ: 'ロ',
    ﾜ: 'ワ',
    ｦ: 'ヲ',
    ﾝ: 'ン',
    ｧ: 'ァ',
    ｨ: 'ィ',
    ｩ: 'ゥ',
    ｪ: 'ェ',
    ｫ: 'ォ',
    ｯ: 'ッ',
    ｬ: 'ャ',
    ｭ: 'ュ',
    ｮ: 'ョ',
    '｡': '。',
    '､': '、',
    ｰ: 'ー',
    '｢': '「',
    '｣': '」',
    '･': '・',
  };
  return str
    .split('')
    .map((char) => {
      if (char === ' ') {
        return '　';
      }
      if (char.charCodeAt(0) >= 0x21 && char.charCodeAt(0) <= 0x7e) {
        return String.fromCharCode(char.charCodeAt(0) + 0xfee0);
      }
      if (kanaMap[char]) {
        return kanaMap[char];
      }
      return char;
    })
    .join('');
};

const toZenkaku = (str: string) => {
  let converted = str;
  // 記号・ASCII・半角カナを順に正規化し、入力の揺れを一回で吸収する。
  converted = toZenkakuAlphaNumMark(converted);
  converted = toFullWidth(converted);
  // NaN対応
  return Number.isNaN(converted) ? str : converted;
};

/**
 * 入力された電話番号のフォーマットを判定する
 * フリーダイヤルや桁不足を除外し、連絡先入力欄の簡易チェックに使う。
 */
const isValidPhoneNumber = (value: string | undefined) => {
  // valueがなければ判定しない
  if (!value) {
    return true;
  }
  if (value.length < 10) return false;
  if (!value.startsWith('0')) return false;
  return !(value.startsWith('0120') || value.startsWith('0800'));
};

/**
 * ブランク文字のみでないことを判定する
 * 見た目上入力されていても空白しか無いケースを弾くために使う。
 */
const isNotBlankCharOnly = (str: string | undefined) => {
  if (!str) {
    return true;
  }
  const space = /^\s+$/;
  return !space.test(str);
};
export {
  toZenkaku,
  toHalfNumber,
  toZenkakuNumber,
  isPastMonth,
  toZenkakuAlpha,
  isNotBlankCharOnly,
  isValidPhoneNumber,
  toZenkakuAlphaNumber,
  toSnakeCase,
  removeCommas,
  addCommas,
};
